import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCommittedTransactionVolumeQuery } from '@/graphql/queries/committedTransactionVolume.generated';
import { useSetCommittedTransactionVolumeMutation } from '@/graphql/mutations/setCommittedTransactionVolume.generated';
import { useRemoveCommittedTransactionVolumeMutation } from '@/graphql/mutations/removeCommittedTransactionVolume.generated';
import i18n from '@/utils/i18n';

// ── Types ────────────────────────────────────────────────────────────────────

type FormState = {
  committed_volume: string;
  discount_per_transaction_in_cents: string;
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCents(cents: number, currency: string): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function isValid(form: FormState): boolean {
  const vol = parseInt(form.committed_volume, 10);
  const disc = parseInt(form.discount_per_transaction_in_cents, 10);
  return !isNaN(vol) && vol > 0 && !isNaN(disc) && disc > 0;
}

// ── Component ────────────────────────────────────────────────────────────────

export function CommittedTransactionVolumeTab({
  storeId,
  currency,
}: {
  storeId: string;
  currency: string;
}) {
  const { data, loading, refetch } = useCommittedTransactionVolumeQuery({
    variables: { store_uuid: storeId },
    skip: !storeId,
  });
  const record = data?.committedTransactionVolume ?? null;

  const [setVolume] = useSetCommittedTransactionVolumeMutation();
  const [removeVolume] = useRemoveCommittedTransactionVolumeMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [form, setForm] = useState<FormState>({
    committed_volume: '',
    discount_per_transaction_in_cents: '',
  });
  const [saving, setSaving] = useState(false);

  const openAdd = () => {
    setForm({ committed_volume: '', discount_per_transaction_in_cents: '' });
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!record) return;
    setForm({
      committed_volume: String(record.committed_volume),
      discount_per_transaction_in_cents: String(
        record.discount_per_transaction_in_cents,
      ),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!isValid(form)) return;
    setSaving(true);
    try {
      await setVolume({
        variables: {
          input: {
            store_uuid: storeId,
            committed_volume: parseInt(form.committed_volume, 10),
            discount_per_transaction_in_cents: parseInt(
              form.discount_per_transaction_in_cents,
              10,
            ),
          },
        },
      });
      await refetch();
      setDialogOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await removeVolume({ variables: { store_uuid: storeId } });
      await refetch();
      setConfirmRemoveOpen(false);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-6 tw:w-full tw:max-w-[872px]">
      <div className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:justify-end">
          {!record && !loading && (
            <Button variant="outline" onClick={openAdd}>
              <Plus className="tw:size-4" />
              {i18n.t('ServicePackage:setCommittedVolume')}
            </Button>
          )}
        </div>

        <Card className="tw:p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="tw:pl-4 tw:h-13">
                  {i18n.t('ServicePackage:committedVolume')}
                </TableHead>
                <TableHead>
                  {i18n.t('ServicePackage:discountPerTransaction')}
                </TableHead>
                <TableHead className="tw:w-[52px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {!record || loading ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="tw:pl-4 tw:py-8 tw:text-sm tw:text-muted-foreground"
                  >
                    {loading
                      ? i18n.t('Common:loading')
                      : i18n.t('ServicePackage:noCommittedVolume')}
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow className="tw:*:first:pl-4 tw:*:last:pr-3">
                  <TableCell>
                    <span className="tw:text-sm">
                      {i18n.t('ServicePackage:nTransactions', {
                        n: record.committed_volume.toLocaleString(),
                      })}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="tw:text-sm">
                      {formatCents(
                        record.discount_per_transaction_in_cents,
                        currency,
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="tw:flex tw:gap-1">
                      <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={openEdit}
                        title={i18n.t('Common:edit')}
                      >
                        <Pencil className="tw:size-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={() => setConfirmRemoveOpen(true)}
                        title={i18n.t('Common:remove')}
                      >
                        <Trash2 className="tw:size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => !saving && setDialogOpen(o)}
      >
        <DialogContent className="tw:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {record
                ? i18n.t('ServicePackage:editCommittedVolume')
                : i18n.t('ServicePackage:setCommittedVolumeTitle')}
            </DialogTitle>
          </DialogHeader>

          <div className="tw:flex tw:flex-col tw:gap-4 tw:py-2">
            <div className="tw:flex tw:flex-col tw:gap-1.5">
              <Label htmlFor="committed_volume">
                {i18n.t('ServicePackage:committedVolumeTransactions')}
              </Label>
              <Input
                id="committed_volume"
                type="number"
                min={1}
                placeholder="e.g. 500"
                value={form.committed_volume}
                onChange={(e) =>
                  setForm((f) => ({ ...f, committed_volume: e.target.value }))
                }
              />
            </div>

            <div className="tw:flex tw:flex-col tw:gap-1.5">
              <Label htmlFor="discount_cents">
                {i18n.t('ServicePackage:discountPerTransactionCents')}
              </Label>
              <Input
                id="discount_cents"
                type="number"
                min={1}
                placeholder="e.g. 10"
                value={form.discount_per_transaction_in_cents}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    discount_per_transaction_in_cents: e.target.value,
                  }))
                }
              />
              {form.discount_per_transaction_in_cents !== '' && (
                <span className="tw:text-xs tw:text-muted-foreground">
                  ={' '}
                  {formatCents(
                    parseInt(form.discount_per_transaction_in_cents, 10) || 0,
                    currency,
                  )}{' '}
                  {i18n.t('ServicePackage:perTransaction')}
                </span>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
            >
              {i18n.t('Common:cancel')}
            </Button>
            <Button onClick={handleSave} disabled={!isValid(form) || saving}>
              {saving ? i18n.t('Common:saving') : i18n.t('Common:save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm removal dialog */}
      <Dialog
        open={confirmRemoveOpen}
        onOpenChange={(o) => !removing && setConfirmRemoveOpen(o)}
      >
        <DialogContent className="tw:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {i18n.t('ServicePackage:removeCommittedVolume')}
            </DialogTitle>
          </DialogHeader>
          <p className="tw:text-sm tw:text-muted-foreground">
            {i18n.t('ServicePackage:removeCommittedVolumeConfirm')}
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmRemoveOpen(false)}
              disabled={removing}
            >
              {i18n.t('Common:cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemove}
              disabled={removing}
            >
              {removing
                ? i18n.t('ServicePackage:removing')
                : i18n.t('Common:remove')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
